import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@one-thd/sui-atomic-components';
import {
  ProductPod,
  ProductImage,
  ProductBadge,
  ProductRating,
  ProductIdentifier,
  PodSection
} from '@thd-olt-component-react/product-pod';
import { extend } from '@thd-nucleus/data-sources';
import { getItemIdForUsedProduct } from '../../hooks/useUsedProductDetail';
import { UsedEquipmentPrice } from './UsedEquipmentPrice';
import { UsedEquipmentAvailability } from './UsedEquipmentAvailability';

// Mobile: row (image-left / details-right) → Desktop: column (image-top / details-bottom)
const podLayout = 'sui-flex sui-flex-row sm:sui-flex-col sui-h-full';
// Image: fixed sidebar on mobile → full-width on desktop
const imageCol = 'sui-w-36 sui-shrink-0 sm:sui-w-full';

const rawUnitPropType = PropTypes.shape({
  suggestedAmt: PropTypes.number,
  retailAmt: PropTypes.number,
  statusCd: PropTypes.number
});

const itemPropType = PropTypes.shape({
  CategoryCd: PropTypes.string,
  SubCategoryCd: PropTypes.string,
  StrNbr: PropTypes.string,
  SuggRetailPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  retailPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  SubCategoryDesc: PropTypes.string,
  title: PropTypes.string,
  rawUnits: PropTypes.arrayOf(rawUnitPropType)
});

export const UsedProductCard = memo(function UsedProductCard({ item, onQuickViewClick }) {
  const itemId = getItemIdForUsedProduct(item);
  // Prefer inventory title so grid and quick view use the same customer-facing name.
  // Catalog productLabel can include extra qualifiers (for example, "rental") that differ.
  const displayTitle = item.title ?? item.SubCategoryDesc ?? 'Unknown Product';

  if (!itemId) {
    return (
      <PodSection
        display={`${podLayout} sm:sui-p-0`}
        fullHeight
        noPadding
      >
        <PodSection
          display={`sui-relative ${imageCol} sui-bg-subtle sui-flex sui-justify-center`}
          columnAlign
          padding="sui-p-2 sm:sui-p-0"
        >
          <div className="sui-w-full sui-aspect-square sui-flex sui-items-center sui-justify-center">
            <Typography color="subtle" variant="body-sm">No Image</Typography>
          </div>
        </PodSection>

        <PodSection
          fullHeight
          columnAlign
          noPadding
        >
          <PodSection noPadding columnAlign alignStart>
            <Typography weight="bold">
              {displayTitle}
            </Typography>
            <UsedEquipmentPrice item={item} />
          </PodSection>
          <PodSection fullHeight noPadding columnAlign alignEnd>
            <UsedEquipmentAvailability item={item} />
            <Button variant="secondary" fullWidth onClick={() => onQuickViewClick(item)}>
              View Used Details
            </Button>
          </PodSection>
        </PodSection>
      </PodSection>
    );
  }

  return (
    <ProductPod
      itemId={itemId}
      analyticsData={{ position: 0, parent: 'UsedEquipment' }}
      render={() => (
        <PodSection
          display={`${podLayout} sm:sui-p-0`}
          fullHeight
          noPadding
        >
          <PodSection
            display={`sui-relative ${imageCol} sui-bg-subtle sui-flex sui-justify-center`}
            columnAlign
            padding="sui-p-2 sm:sui-p-0"
          >
            <ProductImage
              itemId={itemId}
              onClick={(event) => {
                event.preventDefault();
                onQuickViewClick(item);
              }}
            />
          </PodSection>

          <PodSection
            fullHeight
            columnAlign
            noPadding
          >
            <PodSection noPadding columnAlign alignStart>
              <Typography weight="bold">
                {displayTitle}
              </Typography>
              <ProductIdentifier itemId={itemId} type="model" />
              <UsedEquipmentPrice item={item} itemId={itemId} />
            </PodSection>
            <PodSection fullHeight noPadding columnAlign alignEnd>
              <UsedEquipmentAvailability item={item} />
              <Button variant="secondary" fullWidth onClick={() => onQuickViewClick(item)}>
                View Used Details
              </Button>
            </PodSection>
          </PodSection>
        </PodSection>
      )}
    />
  );
});

UsedProductCard.propTypes = {
  item: itemPropType.isRequired,
  onQuickViewClick: PropTypes.func.isRequired
};

UsedProductCard.dataModel = extend(
  ProductPod.dataModel,
  ProductImage.dataModel,
  ProductBadge.dataModel,
  ProductRating.dataModel,
  ProductIdentifier.dataModel
);
